/** Platform admin -> Marketplace -> Fees (docs/Decisions.md D72): fees by status, and crediting one not yet invoiced. */
import { useCallback, useEffect, useState } from 'react';
import * as marketApi from '../api/marketplace';
import { useToast } from './toast';
import FeeTable from './FeeTable';
import { Spinner, ErrorAlert } from './ui';
import { LEDGER_STATUSES, LEDGER_STATUS_LABELS, formatUsd } from '../lib/marketplace';
import { cn, ui } from '../lib/ui';

export default function PlatformFeesPanel() {
  const toast = useToast();
  const [status, setStatus] = useState('pending');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setData(null);
    try {
      setData(await marketApi.listPlatformFees(status));
    } catch (err) {
      setError(err);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  async function credit(fee) {
    const note = window.prompt(`Why credit ${fee.vendorName}'s fee?`);
    if (!note) return;
    try {
      await marketApi.creditFee(fee.entryId, note);
      toast.success('Fee credited');
      load();
    } catch (err) {
      toast.error(err.message || 'Not credited');
    }
  }

  return (
    <section className="mt-8">
      <h2 className="text-title-m">Fees</h2>
      <div className={cn(ui.row, 'mb-4')} role="group" aria-label="Show fees that are">
        {LEDGER_STATUSES.map(s => (
          <button
            key={s}
            type="button"
            className={cn(status === s ? ui.btnPrimary : ui.btnSecondary, ui.btnSm)}
            aria-pressed={status === s}
            onClick={() => setStatus(s)}
          >
            {LEDGER_STATUS_LABELS[s]}
          </button>
        ))}
      </div>
      {error ? (
        <ErrorAlert error={error} />
      ) : !data ? (
        <Spinner label="Loading fees..." />
      ) : (
        <>
          <p className={ui.hint}>
            Platform fees in this view: {formatUsd(data.totals.platformFeeCents)}. An invoiced fee
            is refunded in the Stripe dashboard, not here.
          </p>
          <FeeTable fees={data.fees} label="Fees" show="platform" onCredit={credit} />
        </>
      )}
    </section>
  );
}
